import React, { useCallback } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCropAlt } from '@fortawesome/pro-regular-svg-icons';

import { SceneManager } from 'Components/CanvasEditor/models/SceneManager';

import type { CanvasElement, ImageElement } from 'Components/CanvasEditor/types';
import { CropScene } from 'Components/CanvasEditor/models/CropScene';
import { KonvaImage } from 'Components/CanvasEditor/models/KonvaImage';

interface ImageContentProps {
  selectedElement: ImageElement;
  onElementChange: (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => void;
  sceneManager: SceneManager | null;
  activeCropper: boolean;
  setActiveCropper: Dispatch<SetStateAction<boolean>>;
}

export default function ImageContent({
  selectedElement,
  onElementChange,
  sceneManager,
  activeCropper,
  setActiveCropper,
}: ImageContentProps) {
  const onChangeCornerRadius = (event: ChangeEvent<HTMLInputElement>) =>
    onElementChange({ cornerRadius: event.target.value }, selectedElement.id, true);

  const onApplyMask = () => {
    onElementChange(
      {
        mask: selectedElement.mask !== 'circle' ? 'circle' : 'rect',
      },
      selectedElement.id,
      true
    );
  };

  const syncKonvaToElement = useCallback(
    (konvaImage: KonvaImage, elementId?: string) => {
      const id = elementId || konvaImage.id();

      onElementChange(
        {
          x: konvaImage.x(),
          y: konvaImage.y(),
          width: konvaImage.width(),
          height: konvaImage.height(),
          rotation: konvaImage.rotation(),
          cropX: konvaImage.cropX(),
          cropY: konvaImage.cropY(),
          cropWidth: konvaImage.cropWidth(),
          cropHeight: konvaImage.cropHeight(),
        },
        id,
        true
      );
    },
    [onElementChange]
  );

  const onCrop = () => {
    if (activeCropper) {
      (sceneManager?.getScene(CropScene) as CropScene).endCrop();
    } else {
      sceneManager?.goto(CropScene, { syncCallback: syncKonvaToElement, setActiveCropper });
    }
  };

  return (
    <>
      {selectedElement?.cornerRadius && (
        <input type="number" onChange={onChangeCornerRadius} value={selectedElement.cornerRadius} />
      )}
      <button className="image-mask" onClick={onApplyMask}>
        {selectedElement.mask === 'circle' ? 'Remove' : 'Apply'} mask
      </button>
      <button aria-label="crop" className="image-mask" onClick={onCrop}>
        <FontAwesomeIcon icon={faCropAlt} />
      </button>
    </>
  );
}
